import { useState } from '#app/composables/state';
import { redirect } from '#layers/access/public';
import { delay, TRANSITION_DURATION } from '#layers/base/public';

export function useAuthTransition() {
	const isExpanding = useState('auth-expanding', () => false);

	function triggerExpand() {
		isExpanding.value = true;
	}

	function collapse() {
		isExpanding.value = false;
	}

	return { isExpanding, triggerExpand, collapse };
}

/**
 * Hand off to the browser navigator without any transition. Used for the SSO
 * start legs (`startSsoLogin` / `startSsoSignup`), where we're redirecting to
 * the IdP and the outcome isn't known yet — the expand belongs on confirmed
 * success — and for the link flow under the default layout, where the expand
 * animation has nothing to mount against.
 *
 * `replace: true` swaps the current history entry instead of pushing, dropping
 * the originating auth page from the back stack (used by `startSsoLogin` so a
 * post-login Back skips the login page). The link flow keeps the default push
 * so its in-app originating page stays reachable via Back.
 */
/**
 * Run the expand transition, wait for it to complete, reset the shared flag,
 * then hand off to the browser navigator. The reset prevents the global state
 * from leaking back into the layout on browser back/bfcache restore. Callers
 * today are the login-success path in `pages/auth/login.vue` and the
 * `auth=success` branch of `pages/auth/callback.vue` — both points where
 * success is already confirmed and we're entering the app. Hands off with
 * `replace` so the confirmed-success auth page leaves the back stack.
 */
export async function expandAndRedirect(url: string) {
	const transition = useAuthTransition();
	transition.triggerExpand();
	await delay(TRANSITION_DURATION.BASE);
	transition.collapse();
	redirect(url, { replace: true });
}
